import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { useToast } from '../contexts/ToastContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseBulkCsv } from '../utils/parseBulkCsv';
import StudentForm from '../components/students/StudentForm';
import type { StudentFormData } from '../components/students/StudentForm';
import StudentDetailDrawer from '../components/students/StudentDetailDrawer';
import { PICKUP_OPTIONS } from '../constants';
import { SchoolsResponse, StudentRow, StudentsResponse } from '../types';

export type { GuardianForm } from '../components/students/StudentForm';

function RelationsTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('studentsRelations');
  const [relations, setRelations] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [relationName, setRelationName] = useState('');

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  React.useEffect(() => {
    const list = school?.settings?.relations ?? [];
    setRelations(Array.isArray(list) ? [...list] : []);
  }, [school?.settings?.relations]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = (newList: string[]) => {
    if (!school) return;
    setRelations(newList);
    saveMutation.mutate({
      _id: school._id,
      settings: { ...school.settings, relations: newList },
    });
  };

  const handleAdd = () => {
    setRelationName('');
    setAddOpen(true);
  };
  const handleAddSubmit = () => {
    const name = relationName.trim();
    if (!name) {
      showToast('error', 'Relation name is required');
      return;
    }
    if (relations.includes(name)) {
      showToast('error', 'Relation already exists');
      return;
    }
    handleSave([...relations, name]);
    setAddOpen(false);
  };
  const handleEdit = (idx: number) => {
    setEditingIndex(idx);
    setRelationName(relations[idx] ?? '');
    setEditOpen(true);
  };
  const handleEditSubmit = () => {
    if (editingIndex < 0 || editingIndex >= relations.length) return;
    const name = relationName.trim();
    if (!name) {
      showToast('error', 'Relation name is required');
      return;
    }
    const other = relations.filter((_, i) => i !== editingIndex);
    if (other.includes(name)) {
      showToast('error', 'Relation already exists');
      return;
    }
    const newList = relations.map((r, i) => (i === editingIndex ? name : r));
    handleSave(newList);
    setEditOpen(false);
    setEditingIndex(-1);
  };
  const handleDelete = (idx: number) => {
    if (!window.confirm(`Remove relation "${relations[idx]}"?`)) return;
    handleSave(relations.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Relation
          </Button>
        </Box>
      )}
      {relations.length === 0 ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No relations yet. Add relations (e.g. Mother, Father, Sister, Brother, Uncle); then assign them to guardians when adding or editing students.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Relation</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {relations.map((name, idx) => (
                <TableRow key={idx}>
                  <TableCell>{name}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" title="Edit" onClick={() => handleEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(idx)} disabled={saveMutation.isLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Relation</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Relation" value={relationName} onChange={(e) => setRelationName(e.target.value)} margin="normal" placeholder="e.g. Mother, Father, Sister, Uncle" autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAddSubmit} disabled={saveMutation.isLoading}>Add</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingIndex(-1); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Relation</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Relation" value={relationName} onChange={(e) => setRelationName(e.target.value)} margin="normal" placeholder="e.g. Mother, Father" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditingIndex(-1); }}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleEditSubmit} disabled={saveMutation.isLoading}>Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DispersalMethodTab() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('studentsDispersal');
  const [methods, setMethods] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [methodLabel, setMethodLabel] = useState('');

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];

  React.useEffect(() => {
    const list = school?.settings?.dispersalMethods ?? [];
    setMethods(Array.isArray(list) ? list.map((m) => (typeof m === 'string' ? m : (m as { value?: string; label?: string }).label ?? (m as { value?: string }).value ?? '')) : []);
  }, [school?.settings?.dispersalMethods]);

  const saveMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const handleSave = (newList: string[]) => {
    if (!school) return;
    setMethods(newList);
    saveMutation.mutate({
      _id: school._id,
      settings: { ...school.settings, dispersalMethods: newList },
    });
  };

  const handleAdd = () => {
    setMethodLabel('');
    setAddOpen(true);
  };
  const handleAddSubmit = () => {
    const label = methodLabel.trim();
    if (!label) {
      showToast('error', 'Dispersal method is required');
      return;
    }
    if (methods.includes(label)) {
      showToast('error', 'This dispersal method already exists');
      return;
    }
    handleSave([...methods, label]);
    setAddOpen(false);
  };
  const handleEdit = (idx: number) => {
    setEditingIndex(idx);
    setMethodLabel(methods[idx] ?? '');
    setEditOpen(true);
  };
  const handleEditSubmit = () => {
    if (editingIndex < 0 || editingIndex >= methods.length) return;
    const label = methodLabel.trim();
    if (!label) {
      showToast('error', 'Dispersal method is required');
      return;
    }
    const other = methods.filter((_, i) => i !== editingIndex);
    if (other.includes(label)) {
      showToast('error', 'This dispersal method already exists');
      return;
    }
    const newList = methods.map((m, i) => (i === editingIndex ? label : m));
    handleSave(newList);
    setEditOpen(false);
    setEditingIndex(-1);
  };
  const handleDelete = (idx: number) => {
    if (!window.confirm(`Remove dispersal method "${methods[idx]}"?`)) return;
    handleSave(methods.filter((_, i) => i !== idx));
  };

  return (
    <Box>
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add dispersal method
          </Button>
        </Box>
      )}
      {methods.length === 0 ? (
        <Paper sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No dispersal methods yet. Add methods (e.g. Parent pickup, School bus); they will appear in the student form when adding or editing students.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dispersal method</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {methods.map((name, idx) => (
                <TableRow key={idx}>
                  <TableCell>{name}</TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" title="Edit" onClick={() => handleEdit(idx)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(idx)} disabled={saveMutation.isLoading}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add dispersal method</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Dispersal method" value={methodLabel} onChange={(e) => setMethodLabel(e.target.value)} margin="normal" placeholder="e.g. Parent pickup, School bus" autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAddSubmit} disabled={saveMutation.isLoading}>Add</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditingIndex(-1); }} maxWidth="xs" fullWidth>
        <DialogTitle>Edit dispersal method</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Dispersal method" value={methodLabel} onChange={(e) => setMethodLabel(e.target.value)} margin="normal" placeholder="e.g. Parent pickup" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditingIndex(-1); }}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleEditSubmit} disabled={saveMutation.isLoading}>Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const Students: React.FC = () => {
  const user = authService.getStoredUser();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [classFilter, setClassFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [singleOpen, setSingleOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentRow | null>(null);
  const [detailStudent, setDetailStudent] = useState<StudentRow | null>(null);
  const [addFormKey, setAddFormKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const studentsSectionTab = location.pathname.includes('/relations') ? 1 : location.pathname.includes('/dispersal') ? 2 : 0;

  const params = new URLSearchParams();
  if (classFilter) params.set('class', classFilter);
  if (sectionFilter) params.set('section', sectionFilter);

  const { data, isLoading } = useQuery<StudentsResponse>(
    ['students', classFilter, sectionFilter],
    async () => {
      const res = await apiClient.get(`/students?${params.toString()}`);
      return res.data as StudentsResponse;
    }
  );
  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];
  const classSections = school?.settings?.classSections ?? [];
  const relationsList: string[] = school?.settings?.relations ?? [];
  const dispersalMethodsList: string[] = school?.settings?.dispersalMethods ?? [];
  const pickupOptions = dispersalMethodsList.length > 0 ? dispersalMethodsList.map((l) => ({ value: l, label: l })) : PICKUP_OPTIONS;
  const getPickupLabel = (value: string) => pickupOptions.find((o) => o.value === value)?.label ?? value;
  const students: StudentRow[] = data?.students ?? [];
  const classes: string[] = classSections.length > 0
    ? Array.from(new Set(classSections.map((cs) => cs.class))).sort()
    : Array.from(new Set(students.map((s) => s.class).filter((x): x is string => !!x))).sort();
  const sections: string[] = Array.from(new Set(students.map((s) => s.section).filter((x): x is string => !!x))).sort();

  const isPrincipal = user && canAccessPrincipalFeatures(user.role);
  const { canView: canViewStudents, canEdit: canEditStudents, loading: featureAccessLoading } = useFeatureAccess('students');
  const { canView: canViewList } = useFeatureAccess('studentsList');
  const { canView: canViewRelations } = useFeatureAccess('studentsRelations');
  const { canView: canViewDispersal } = useFeatureAccess('studentsDispersal');
  React.useEffect(() => {
    if (featureAccessLoading) return;
    if (!canViewStudents && !canEditStudents) navigate('/', { replace: true });
  }, [canViewStudents, canEditStudents, featureAccessLoading, navigate]);
  React.useEffect(() => {
    if (isPrincipal || featureAccessLoading) return;
    if (studentsSectionTab === 0 && !canViewList) {
      if (canViewRelations) navigate('/students/relations', { replace: true });
      else if (canViewDispersal) navigate('/students/dispersal', { replace: true });
      else if (!canViewList && !canViewRelations && !canViewDispersal) navigate('/', { replace: true });
    } else if (studentsSectionTab === 1 && !canViewRelations) {
      if (canViewList) navigate('/students', { replace: true });
      else if (canViewDispersal) navigate('/students/dispersal', { replace: true });
      else navigate('/', { replace: true });
    } else if (studentsSectionTab === 2 && !canViewDispersal) {
      if (canViewList) navigate('/students', { replace: true });
      else if (canViewRelations) navigate('/students/relations', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [isPrincipal, featureAccessLoading, studentsSectionTab, canViewList, canViewRelations, canViewDispersal, navigate]);

  const createMutation = useMutation(
    async (body: StudentFormData) => {
      const res = await apiClient.post('/students', body);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students');
        showToast('success', 'Student added.');
        setSingleOpen(false);
        setAddFormKey((k) => k + 1);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to add student');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, body }: { id: string; body: StudentFormData | Record<string, unknown> }) => {
      const res = await apiClient.put(`/students/${id}`, body);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students');
        showToast('success', 'Student updated.');
        setEditOpen(false);
        setEditingStudent(null);
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to update student');
      },
    }
  );

  const blockMutation = useMutation(
    async ({ id, disable }: { id: string; disable: boolean }) => {
      const res = await apiClient.put(`/students/${id}/disable-dispersal`, { disable });
      return res.data;
    },
    {
      onSuccess: (_, { disable }) => {
        queryClient.invalidateQueries('students');
        showToast('success', disable ? 'Student blocked (dispersal disabled).' : 'Student unblocked.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to update');
      },
    }
  );

  const bulkMutation = useMutation(
    async (students: { studentId: string; name: string; class: string; section: string; pickupMethod: string }[]) => {
      const res = await apiClient.post('/students/bulk', { students });
      return res.data as { created: number; errors?: { row: number; studentId: string; message: string }[] };
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('students');
        const msg = data.errors?.length
          ? `Created ${data.created}. ${data.errors.length} row(s) had errors.`
          : `Created ${data.created} student(s).`;
        showToast('success', msg);
        setBulkOpen(false);
        setBulkText('');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Bulk add failed');
      },
    }
  );

  const bulkRows = bulkText ? parseBulkCsv(bulkText) : [];

  if (isLoading) {
    return (
      <Box>
        <Typography>Loading students...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Students
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isPrincipal
          ? 'You see all students for your school. Use Settings to control what other staff can see.'
          : 'Students you can view based on your school access settings.'}
      </Typography>

      <Tabs
        value={studentsSectionTab}
        onChange={(_, v) => {
          if (v === 0) navigate('/students');
          else if (v === 1) navigate('/students/relations');
          else navigate('/students/dispersal');
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Students" />
        <Tab label="Relations" />
        <Tab label="Dispersal method" />
      </Tabs>

      {studentsSectionTab === 1 && <RelationsTab />}
      {studentsSectionTab === 2 && <DispersalMethodTab />}

      {studentsSectionTab === 0 && (
        <>
      {(isPrincipal || canEditStudents) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSingleOpen(true)}>
            Add student
          </Button>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setBulkOpen(true)}>
            Bulk add
          </Button>
        </Box>
      )}

      {students.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Class"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="">All</MenuItem>
            {classes.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Section"
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="">All</MenuItem>
            {sections.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {students.length === 0 ? (
        <Box sx={{ py: 3, px: 2, bgcolor: 'info.light', color: 'info.contrastText', borderRadius: 1 }}>
          <Typography variant="body2">
            No students to show. You may have no access to the student list (check Settings), or there are no students for your school or your assigned class.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student ID</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Guardians</TableCell>
                <TableCell>Dispersal</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right" sx={{ width: 56 }}>View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s) => (
                <TableRow
                  key={s._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDetailStudent(s)}
                >
                  <TableCell>{s.studentId}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.class}</TableCell>
                  <TableCell>{s.section || '–'}</TableCell>
                  <TableCell>{(s.guardians?.length ?? 0) > 0 ? `${s.guardians?.length} guardian(s)` : '–'}</TableCell>
                  <TableCell><Chip label={getPickupLabel(s.pickupMethod ?? '')} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">
                    {(s.isDispersalDisabled ?? s.isDismissalDisabled) ? (
                      <Chip label="Blocked" size="small" color="warning" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" title="View details" onClick={() => setDetailStudent(s)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <StudentDetailDrawer
        student={detailStudent}
        open={!!detailStudent}
        onClose={() => setDetailStudent(null)}
        onEdit={(s) => {
          setEditingStudent(s);
          setDetailStudent(null);
          setEditOpen(true);
        }}
        onBlock={(s) => {
          blockMutation.mutate({ id: String(s._id), disable: !(s.isDispersalDisabled ?? s.isDismissalDisabled) });
          setDetailStudent(null);
        }}
        canEdit={!!(isPrincipal || canEditStudents)}
        blockLoading={blockMutation.isLoading}
        pickupOptions={pickupOptions}
      />

      {students.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Total: {students.length} student(s)
        </Typography>
      )}

      <StudentForm
        key={addFormKey}
        open={singleOpen}
        onClose={() => setSingleOpen(false)}
        mode="add"
        student={null}
        classSections={classSections}
        relationsList={relationsList}
        pickupOptions={pickupOptions}
        canEdit={!!(isPrincipal || canEditStudents)}
        saving={createMutation.isLoading}
        onSave={(data) => createMutation.mutate(data)}
      />

      <StudentForm
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingStudent(null); }}
        mode="edit"
        student={editingStudent}
        classSections={classSections}
        relationsList={relationsList}
        pickupOptions={pickupOptions}
        canEdit={!!(isPrincipal || canEditStudents)}
        saving={updateMutation.isLoading}
        onSave={(data) => {
          if (!editingStudent?._id) {
            showToast('error', 'Name and class are required.');
            return;
          }
          updateMutation.mutate({
            id: String(editingStudent._id),
            body: data,
          });
        }}
      />

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk add students</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            One student per line. Columns: studentId, name, class, section (optional), pickupMethod (optional). Use comma to separate. Header row optional. Example:
          </Typography>
          <Typography component="pre" variant="caption" sx={{ display: 'block', mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            {'STU001,John Doe,1,A,PARENT_PICKUP\nSTU002,Jane Smith,2,B'}
          </Typography>
          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder="Paste CSV here..."
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          />
          {bulkRows.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {bulkRows.length} row(s) to add
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          {(isPrincipal || canEditStudents) && (
          <Button
            variant="contained"
            onClick={() => bulkMutation.mutate(bulkRows)}
            disabled={bulkRows.length === 0 || bulkMutation.isLoading}
          >
            Add {bulkRows.length} student(s)
          </Button>
          )}
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default Students;
